export enum WorkOrderStatus {
    PendingFieldSignature = 1,
    PendingPO = 2,
    ReceivedPO = 3,
    Invoiced = 4,
    Voided = 5,
    NoCharge = 6
}

export type WorkOrderTrade = "Drywall" | "Paint" | "Other";

export interface WorkOrderCounts {
    pendingFieldSignature: number;
    pendingPO: number;
    receivedPO: number;
    invoiced: number;
    voided: number;
    noCharge: number;
}

export interface WorkOrderListItem {
    workOrderID: number;
    altWorkOrderNumber: string | null;
    projectID: number;
    customerID: number | null;
    contactName: string;
    city: string | null;
    department: string;
    departmentOther: string | null;
    employeeID: number;
    wcdpSupervisorEmployeeID: number | null;
    status: number;
    purchaseOrderNumber: string | null;
    invoiceNumber: string | null;
    coNumber: string | null;
    noCharge: boolean;
    /** Billing only — hour claims blocked (TAS-442). */
    billingOnly: boolean;
    totalAmount: number | null;
    /** Customer-negotiated discount (TAS-557); amount due = totalAmount - discountAmount. */
    discountAmount: number;
    totalHours: number | null;
    createdAt: string;
    updatedAt: string | null;
    employeeName: string;
    wcdpSupervisorName: string | null;
    projectName: string;
    altProjectID: string;
    trade: WorkOrderTrade;
    noChargeAt: string | null;
    voidedAt: string | null;
    firstItemDescription: string | null;
    itemCount: number;
    // Change order membership (null when not in a CO)
    changeOrderID: number | null;
    changeOrderCORNumber: number | null;
}

export interface WorkOrderItem {
    workOrderItemID: number;
    workOrderID: number;
    itemNumber: number;
    description: string;
    reasonForCorrection: string;
    lotsUnit: string;
    hours: number;
    hourlyRate: number;
    totalAmount: number;
    accountCodeID: number | null;
}

export interface WorkOrderItemPhoto {
    workOrderItemPhotoID: number;
    workOrderItemID: number;
    photoNumber: number;
    blobUrl: string;
    originalFileName: string | null;
    createdAt: string;
    createdAtOffset: string;
}

export interface WorkOrderItemWithPhotos extends WorkOrderItem {
    photos: WorkOrderItemPhoto[];
}

export interface WorkOrderStatusHistory {
    workOrderStatusHistoryID: number;
    workOrderID: number;
    fromStatus: number | null;
    toStatus: number;
    changedByAdminEmployeeID: number | null;
    changedByEmployeeID: number | null;
    notes: string | null;
    createdAt: string;
    createdAtOffset: string;
    changedByName: string | null;
}

export interface WorkOrderPdfVersion {
    workOrderPdfVersionID: number;
    workOrderID: number;
    versionNumber: number;
    pdfBlobUrl: string;
    generatedReason: string;
    generatedByEmployeeID: number | null;
    generatedByAdminEmployeeID: number | null;
    createdAt: string;
    createdAtOffset: string;
}

export interface WorkOrderCustomerSignedDocVersion {
    workOrderCustomerSignedDocVersionID: number;
    workOrderID: number;
    versionNumber: number;
    documentBlobUrl: string;
    uploadedByAdminEmployeeID: number;
    notes: string | null;
    createdAt: string;
    createdAtOffset: string;
}

/** Employee hour claim against a WO task (TAS-293); rate/amount are the claim-time snapshot. */
export interface WorkOrderHourClaim {
    employeeWorkOrderTaskID: number;
    workOrderTaskID: number;
    employeeID: number;
    altEmployeeID: string | null;
    employeeName: string;
    jobTitle: string | null;
    entryDate: string;
    payPeriodID: number;
    hoursClaimed: number;
    hourlyRate: number;
    rateSource: 'override' | 'system' | 'legacy';
    amount: number;
    submitted: boolean;
    insertDateTime: string;
    insertDateTimeOffset: string;
}

export interface WorkOrderDetail extends WorkOrderListItem {
    employeeSignature: string;
    customerSignature: string | null;
    customerSignatureDate: string | null;
    customerSignatureDateOffset: string | null;
    customerSignatureMethod: string | null;
    /** Who signed, as they printed it, and their title. Null once a signature is removed. */
    customerPrintedName: string | null;
    customerTitle: string | null;
    customerEmail: string | null;
    customerSignedDocumentBlobUrl: string | null;
    pdfBlobUrl: string | null;
    voidedAtOffset: string | null;
    voidedByAdminEmployeeID: number | null;
    voidReason: string | null;
    noChargeAtOffset: string | null;
    noChargeByAdminEmployeeID: number | null;
    noChargeReason: string | null;
    createdAtOffset: string;
    updatedAtOffset: string | null;
    items: WorkOrderItemWithPhotos[];
    statusHistory: WorkOrderStatusHistory[];
    pdfVersions: WorkOrderPdfVersion[];
    customerSignedDocVersions: WorkOrderCustomerSignedDocVersion[];
    hourClaims?: WorkOrderHourClaim[];
    isInChangeOrder: boolean;
    changeOrderID: number | null;
    changeOrderCORNumber: number | null;
}
